import { useCallback, useEffect, useState } from 'react';

import lodash from 'lodash';
import { Prompt, useHistory, useParams } from 'react-router-dom';

import WarningModal from '~/components/common/WarningModal';
import PatientProfile from '~/components/maud/patients/profile/PatientProfile';
import { ApiCommand } from '~/lib/Api';
import { PatientTabs } from '~/lib/constants/patients.constants';
import { PatientTabsCollection } from '~/lib/hooks/usePatientTabsCollection';
import { OrganizeBy } from '~/lib/constants/task.constants';
import urlConstants from '~/lib/constants/url.constants';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useApi, usePrevious } from '~/lib/hooks';
import { FetchTasksResponse, TaskStatusLabel } from '~/lib/util/task.util';
import { EditNoteDrawerPortal } from '~/components/common/EditNoteDrawerPortal';
import { useGetPatientQuery } from '~/store/services/patients';
import PageNotFound from '~/pages/404';
import Loading from '~/pages/loading';
import { isValidUUID } from '~/lib/utils';

const ALL_TASKS_PAGE_SIZE = 3;

const { listTasks: listTasksUrl } = urlConstants.tasks;

const PatientPage = () => {
  const history = useHistory();
  const params = useParams();
  const [
    patientId,
    tab = PatientTabsCollection[PatientTabs.OVERVIEW].subPath,
    detailsTabId,
  ] = params[0].split('/');

  const isInvalidPatientId = !patientId || !isValidUUID(patientId);

  const { data: getPatientResponse, isLoading: isGetPatientLoading } =
    useGetPatientQuery(
      { patientId },
      {
        refetchOnMountOrArgChange: true,
        skip: isInvalidPatientId,
      },
    );
  const { patientHasUnsavedChanges, setComponentVisibility } =
    useComponentPortalContext();

  const [isWarningModalOpen, setIsWarningModalOpen] = useState<boolean>(false);
  const [allowNavigation, setAllowNavigation] = useState<boolean>(false);
  const [intendedRoute, setIntendedRoute] = useState<string>('');
  const [selectedTaskTab, setSelectedTaskTab] = useState<number>(0);
  const lastUUID: string = usePrevious<string>(patientId);

  const {
    data: openMaudTasksData,
    error: openMaudTasksError,
    sendRequest: getOpenMaudTasks,
  } = useApi<FetchTasksResponse>();
  const {
    data: openFamilyTasksData,
    error: openFamilyTasksError,
    sendRequest: getOpenFamilyTasks,
  } = useApi<FetchTasksResponse>();

  const fetchTasks = useCallback((): void => {
    if (isInvalidPatientId) return;
    const maudTaskOptions = {
      'filter-by': {
        'is-family-task': false,
        'patient-external-id': patientId,
        status: [TaskStatusLabel.TO_DO, TaskStatusLabel.IN_PROGRESS],
      },
      'order-by': OrganizeBy.DUE_DATE,
      'page-number': 1,
      'page-size': ALL_TASKS_PAGE_SIZE,
    };
    const familyTaskOptions = lodash.cloneDeep(maudTaskOptions);
    familyTaskOptions['filter-by']['is-family-task'] = true;
    familyTaskOptions['order-by'] = OrganizeBy.CREATED_DATE;

    const baseParams = {
      command: ApiCommand.GET,
      url: listTasksUrl,
    };
    getOpenMaudTasks({
      ...baseParams,
      options: maudTaskOptions,
    });
    getOpenFamilyTasks({
      ...baseParams,
      options: familyTaskOptions,
    });
  }, [patientId]);

  useEffect(() => {
    if (allowNavigation && intendedRoute) {
      history.push(intendedRoute);
      setAllowNavigation(false);
      setIntendedRoute('');
      setIsWarningModalOpen(false);
    }
  }, [allowNavigation, intendedRoute]);

  useEffect(() => {
    if (!patientHasUnsavedChanges && isWarningModalOpen) {
      setIsWarningModalOpen(false);
    }
  }, [isWarningModalOpen, patientHasUnsavedChanges]);

  useEffect(() => {
    if (isInvalidPatientId) return;
    if (lodash.isNil(detailsTabId) || lastUUID !== patientId) {
      setSelectedTaskTab(0); // Reseting the default tab on patient change
      fetchTasks();
    }
  }, [patientId, detailsTabId]);

  useEffect(() => {
    if (openMaudTasksData !== null && openFamilyTasksData !== null) {
      setSelectedTaskTab(
        // eslint-disable-next-line no-nested-ternary
        openMaudTasksData?.totalCount > 0
          ? 0
          : openFamilyTasksData?.totalCount > 0
            ? 1
            : 0,
      );
    }
  }, [openMaudTasksData?.totalCount, openFamilyTasksData?.totalCount]);

  const handleConfirmNavigation = () => {
    setAllowNavigation(true);
  };

  return (
    <>
      <Prompt
        message={(location): string | boolean => {
          const { pathname } = location;
          const [_blank, _maud, nextRoute, newPatientId] = pathname.split('/');
          const allowNav =
            !patientHasUnsavedChanges ||
            (nextRoute === 'patients' && newPatientId === patientId);

          setIntendedRoute(pathname);

          if (allowNav) {
            setComponentVisibility({
              component: PortalComponent.EDIT_CARE_TEAM,
              hasUnsavedChanges: false,
            });
          }

          setIsWarningModalOpen(!allowNav);

          return allowNav;
        }}
        when={!allowNavigation}
      />
      <WarningModal
        cancelButtonText="Return to editing"
        confirmButtonText="Discard changes"
        description="You're exiting this page without saving your changes. Are you sure you want to leave this page?"
        id="general-discard-changes"
        isOpen={isWarningModalOpen}
        onClickCancel={() => setIsWarningModalOpen(false)}
        onClickConfirm={handleConfirmNavigation}
        onClose={() => setIsWarningModalOpen(false)}
        title="Warning"
      />
      <>
        {(() => {
          if (isInvalidPatientId) return <PageNotFound />;
          if (isGetPatientLoading) return <Loading />;
          if (!getPatientResponse) return <PageNotFound />;
          return (
            <PatientProfile
              detailsTabId={detailsTabId}
              familyTasksData={openFamilyTasksData}
              fetchTasksData={fetchTasks}
              isTasksError={!!openMaudTasksError || !!openFamilyTasksError}
              maudTasksData={openMaudTasksData}
              patient={getPatientResponse}
              selectedTaskTab={selectedTaskTab}
              setSelectedTaskTab={setSelectedTaskTab}
              tab={tab}
            />
          );
        })()}
        <EditNoteDrawerPortal />
      </>
    </>
  );
};

export default PatientPage;
